import { buildStructuredFilter } from '@/entities/(filter)/common';
import type { HeySynclyChat, HeySynclyChatMessage } from '@/entities/hey-syncly';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';

const hasFilterPayload = (filter?: StructuredFilter | null): filter is StructuredFilter =>
  Boolean(filter && Object.keys(filter).length > 0);

const getDateFilterValue = (
  formats: StructuredFilterQueryFormatT[],
  columnKey: FEEDBACK_COLUMNS.DATE_START | FEEDBACK_COLUMNS.DATE_END,
) => {
  const value = formats
    .find((format) => format.columnKey === columnKey)
    ?.filterValues.at(0)?.filterKey;

  return typeof value === 'string' ? value : null;
};

const buildFilterFromFilterString = (filterString: string): SFilter | null => {
  if (!filterString) return null;

  try {
    const parsed = JSON.parse(filterString) as StructuredFilterQueryFormatT[];
    if (!Array.isArray(parsed)) return null;

    const dataFormats = parsed.filter(
      (format) =>
        format.columnKey !== FEEDBACK_COLUMNS.DATE_START &&
        format.columnKey !== FEEDBACK_COLUMNS.DATE_END,
    );

    const startDate = getDateFilterValue(parsed, FEEDBACK_COLUMNS.DATE_START);
    const endDate = getDateFilterValue(parsed, FEEDBACK_COLUMNS.DATE_END);

    const sFilter = new SFilter(buildStructuredFilter(dataFormats));

    if (startDate || endDate) {
      sFilter.setDateFilter({
        start_date: startDate,
        end_date: endDate,
      });
    }

    return sFilter.get() ? sFilter : null;
  } catch {
    return null;
  }
};

const parseFilterFormats = (
  filterString: string,
): StructuredFilterQueryFormatT[] | null => {
  if (!filterString) return null;

  try {
    const parsed = JSON.parse(filterString) as StructuredFilterQueryFormatT[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const buildFilterFromMessage = (message?: HeySynclyChatMessage): SFilter | null => {
  if (!message) return null;

  if (hasFilterPayload(message.filter)) {
    return new SFilter(message.filter);
  }

  return buildFilterFromFilterString(message.filter_string);
};

export const getLatestChatFilterSnapshot = (
  chat: HeySynclyChat | null,
): {
  filter: SFilter | null;
  formats: StructuredFilterQueryFormatT[] | null;
} | null => {
  if (!chat) return null;

  const latestFilteredMessage = [...chat.messages]
    .reverse()
    .find(
      (message) =>
        hasFilterPayload(message.filter) || Boolean(message.filter_string),
    );

  if (!latestFilteredMessage) {
    return null;
  }

  return {
    filter: buildFilterFromMessage(latestFilteredMessage),
    formats: parseFilterFormats(latestFilteredMessage?.filter_string || ''),
  };
};
